import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import Seo from "@/components/seo/Seo";
import { publicApi } from "@/lib/api";
import serviceContent from "@/data/serviceContent.json";
import { SERVICE_SEO_CONTENT } from "@/data/serviceSeoContent";
import featureFlags from "@/config/featureFlags.json";

const NORMA_LABELS = {
  "iso-9001": "ISO 9001",
  "iso-14001": "ISO 14001",
  "isoiec-27001": "ISO/IEC 27001",
  "iso-27001": "ISO/IEC 27001",
  fsc: "FSC",
  "inte-g38": "INTE G38",
  "bandera-azul-ecologica": "Bandera Azul Ecológica",
};

export default function ServicioDetalle() {
  const { slug } = useParams();
  const service = serviceContent[slug];
  const [cases, setCases] = useState([]);

  useEffect(() => {
    if (!featureFlags.showSuccessCases || !service) return;
    publicApi.casos({ servicio: service.titulo })
      .then((items) => setCases(items.slice(0, 3)))
      .catch(() => setCases([]));
  }, [service]);

  if (!service) {
    return (
      <main className="pt-32 text-center min-h-[60vh]">
        <Seo title="Servicio no encontrado" description="El servicio solicitado no está disponible." path={`/servicios/${slug}`} noindex />
        Servicio no encontrado. <Link to="/#servicios" className="underline">Ver servicios</Link>
      </main>
    );
  }

  const path = `/servicios/${slug}`;
  const seoContent = SERVICE_SEO_CONTENT[slug];
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: service.titulo,
      description: service.descripcion,
      url: `https://www.kbconsultoria.com${path}`,
      provider: { "@type": "Organization", "@id": "https://www.kbconsultoria.com/#organization", name: "KB Consultoría", url: "https://www.kbconsultoria.com/" },
      areaServed: ["Costa Rica", "Centroamérica"],
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Inicio", item: "https://www.kbconsultoria.com/" },
        { "@type": "ListItem", position: 2, name: "Servicios", item: "https://www.kbconsultoria.com/#servicios" },
        { "@type": "ListItem", position: 3, name: service.titulo, item: `https://www.kbconsultoria.com${path}` },
      ],
    },
  ];
  if (seoContent?.faq?.length) {
    schema.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: seoContent.faq.map(([question, answer]) => ({
        "@type": "Question",
        name: question,
        acceptedAnswer: { "@type": "Answer", text: answer },
      })),
    });
  }

  return (
    <main className="pt-[72px]">
      <Seo title={`${service.titulo} en Costa Rica`} description={service.descripcion} path={path} structuredData={schema} />
      <div className="mx-auto max-w-[1440px] px-5 md:px-10 py-8">
        <Link to="/#servicios" className="inline-flex items-center gap-2 eyebrow text-[#4A4A4A]">
          <ArrowLeft className="w-4 h-4" /> Todos los servicios
        </Link>
      </div>

      <header className="border-b border-[#1A1A1A]/10 py-16 md:py-24">
        <div className="mx-auto max-w-[1200px] px-5 md:px-10">
          <div className="eyebrow mb-6">Consultoría en Costa Rica y Centroamérica</div>
          <h1 className="display-1 max-w-5xl">{service.titulo}</h1>
          <p className="mt-8 max-w-3xl font-serif text-2xl md:text-3xl leading-tight text-[#1B3B2B]">{service.descripcion}</p>
        </div>
      </header>

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1200px] px-5 md:px-10 grid grid-cols-12 gap-8 md:gap-12">
          <div className="col-span-12 md:col-span-8">
            <h2 className="font-serif text-3xl md:text-4xl">Beneficios del servicio</h2>
            <ul className="mt-6 grid sm:grid-cols-2 gap-3">
              {service.beneficios.map((item) => (
                <li key={item} className="flex gap-3 border border-[#1A1A1A]/10 p-5">
                  <Check className="w-4 h-4 mt-1 shrink-0 text-[#1B3B2B]" /> {item}
                </li>
              ))}
            </ul>

            <h2 className="mt-16 font-serif text-3xl md:text-4xl">Entregables habituales</h2>
            <ol className="mt-6 space-y-3">
              {service.entregables.map((item, index) => (
                <li key={item} className="flex gap-5 py-4 border-b border-[#1A1A1A]/10">
                  <span className="eyebrow text-[#1B3B2B]">{String(index + 1).padStart(2, "0")}</span>
                  <span>{item}</span>
                </li>
              ))}
            </ol>

            {seoContent?.proceso && (
              <section className="mt-16" aria-labelledby="service-process-title">
                <h2 id="service-process-title" className="font-serif text-3xl md:text-4xl">Cómo funciona el servicio</h2>
                <ol className="mt-6 grid sm:grid-cols-2 gap-3">
                  {seoContent.proceso.map((item, index) => (
                    <li key={item} className="border border-[#1A1A1A]/10 p-5">
                      <span className="eyebrow text-[#1B3B2B]">{String(index + 1).padStart(2, "0")}</span>
                      <div className="mt-2">{item}</div>
                    </li>
                  ))}
                </ol>
              </section>
            )}

            <h2 className="mt-16 font-serif text-3xl md:text-4xl">Normas relacionadas</h2>
            <div className="mt-6 flex flex-wrap gap-3">
              {service.normas.map((normaSlug) => (
                <Link key={normaSlug} to={`/normas/${normaSlug === "isoiec-27001" ? "iso-27001" : normaSlug}`} className="kb-btn ghost">
                  {NORMA_LABELS[normaSlug]} <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              ))}
            </div>

            {seoContent?.faq && (
              <section className="mt-16" aria-labelledby="service-faq-title">
                <h2 id="service-faq-title" className="font-serif text-3xl md:text-4xl">Preguntas frecuentes</h2>
                <div className="mt-6 divide-y divide-[#1A1A1A]/10 border-y border-[#1A1A1A]/10">
                  {seoContent.faq.map(([question, answer]) => (
                    <article key={question} className="py-6">
                      <h3 className="font-serif text-2xl">{question}</h3>
                      <p className="mt-3 leading-relaxed text-[#4A4A4A]">{answer}</p>
                    </article>
                  ))}
                </div>
              </section>
            )}

            {featureFlags.showSuccessCases && cases.length > 0 && (
              <div className="mt-16">
                <h2 className="font-serif text-3xl md:text-4xl">Casos relacionados</h2>
                <div className="mt-6 space-y-3">
                  {cases.map((item) => (
                    <Link key={item.id} to={`/casos-de-exito/${item.slug}`} className="flex justify-between gap-4 py-4 border-b border-[#1A1A1A]/10 link-underline">
                      <span>{item.titulo}</span><ArrowUpRight className="w-4 h-4 shrink-0" />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="col-span-12 md:col-span-4">
            <div className="sticky top-32 bg-[#EFEBE2] border border-[#1A1A1A]/10 p-8">
              <div className="eyebrow mb-4">Conversemos</div>
              <p className="font-serif text-2xl leading-tight">Definamos un alcance adaptado a su organización.</p>
              <Link to="/#contacto" className="kb-btn mt-6 inline-flex">Solicitar asesoría <ArrowUpRight className="w-3.5 h-3.5" /></Link>
              {featureFlags.showSuccessCases && <Link to="/casos-de-exito" className="mt-5 block text-sm underline">Conocer casos de éxito</Link>}
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}
